// resources/js/admin/pages/module-pages/locales/LocalesPage.tsx

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, X, Eye, EyeOff, Edit, Trash2 } from "lucide-react";
import PageListLayout from "../../../layouts/PageListLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import LocalesList from "../../../components/shared/locales/LocalesList";
import { Locale } from "../../../types/features/Locale";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import useLocales from "../../../hooks/features/locales/useLocales";

export default function LocalesPage() {
    const navigate = useNavigate();
    const { locales, loading, deleteLocale, toggleActive } = useLocales();
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [selectedLocale, setSelectedLocale] = useState<Locale | null>(null);
    const [localeToDelete, setLocaleToDelete] = useState<Locale | null>(null);
    const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>("");
    const [filterRtl, setFilterRtl] = useState<string>("");

    // Filtrer les locales
    const filteredLocales = useMemo(() => {
        let result = locales;

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            result = result.filter(
                (l) =>
                    l.name.toLowerCase().includes(searchLower) ||
                    l.code.toLowerCase().includes(searchLower)
            );
        }

        if (filterStatus) {
            result = result.filter((l) => l.is_active === (filterStatus === "active"));
        }

        if (filterRtl) {
            result = result.filter((l) => l.is_rtl === (filterRtl === "rtl"));
        }

        return result;
    }, [locales, searchTerm, filterStatus, filterRtl]);

    // Stats
    const stats = {
        total: locales.length,
        active: locales.filter((l) => l.is_active).length,
        inactive: locales.filter((l) => !l.is_active).length,
        rtl: locales.filter((l) => l.is_rtl).length,
    };

    const handleToggleActive = async (locale: Locale) => {
        const success = await toggleActive(locale.id, locale.is_active);
        if (success) {
            alertSuccess(locale.is_active ? "Langue désactivée" : "Langue activée");
        } else {
            alertError("Erreur lors du changement de statut");
        }
    };

    const handleDelete = async () => {
        if (!localeToDelete) return;
        setIsDeleting(true);
        const success = await deleteLocale(localeToDelete.id);
        setIsDeleting(false);
        if (success) {
            setLocaleToDelete(null);
            alertSuccess("Langue supprimée avec succès");
        } else {
            alertError("Erreur lors de la suppression");
        }
    };

    const handleDeleteMany = async () => {
        if (!itemsToDelete || itemsToDelete.length === 0) return;
        let allSuccess = true;
        for (const id of itemsToDelete) {
            const success = await deleteLocale(id);
            if (!success) allSuccess = false;
        }
        if (allSuccess) {
            setItemsToDelete(null);
            alertSuccess(`${itemsToDelete.length} langues supprimées`);
        } else {
            alertError("Erreur lors de la suppression multiple");
        }
    };

    const clearFilters = () => {
        setSearchTerm("");
        setFilterStatus("");
        setFilterRtl("");
    };

    const hasActiveFilters = searchTerm || filterStatus || filterRtl;

    const getActionsMenu = (locale: Locale): Menu[] => {
        return [
            {
                label: locale.is_active ? "Désactiver" : "Activer",
                icon: locale.is_active ? EyeOff : Eye,
                onClick: () => handleToggleActive(locale),
            },
            {
                label: "Modifier",
                icon: Edit,
                onClick: () => navigate(`/admin/locales/update?id=${locale.id}`),
            },
            {
                label: "Supprimer",
                icon: Trash2,
                onClick: () => setLocaleToDelete(locale),
            },
        ];
    };

    return (
        <PageListLayout title="Gestion des langues">
            <div className="px-6 py-4 bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Langues</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Gérez les langues disponibles pour les sites
                            </p>
                        </div>
                        <button
                            onClick={() => navigate("/admin/locales/new")}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                        >
                            <Plus size={18} />
                            Nouvelle langue
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 mt-6">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                            <p className="text-xs text-blue-700/70">Total</p>
                            <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                            <p className="text-xs text-green-700/70">Actives</p>
                            <p className="text-2xl font-bold text-green-700">{stats.active}</p>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
                            <p className="text-xs text-gray-700/70">Inactives</p>
                            <p className="text-2xl font-bold text-gray-700">{stats.inactive}</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                            <p className="text-xs text-purple-700/70">RTL</p>
                            <p className="text-2xl font-bold text-purple-700">{stats.rtl}</p>
                        </div>
                    </div>

                    {/* Recherche */}
                    <div className="relative mt-6">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher une langue..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                        />
                    </div>

                    {/* Filtres */}
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                                showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                            <Filter size={18} /> Filtrer
                            {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
                        </button>

                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-700"
                            >
                                <X size={14} /> Effacer tout
                            </button>
                        )}
                    </div>

                    {showFilters && (
                        <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="">Tous</option>
                                        <option value="active">Actives</option>
                                        <option value="inactive">Inactives</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
                                    <select
                                        value={filterRtl}
                                        onChange={(e) => setFilterRtl(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="">Toutes</option>
                                        <option value="ltr">Gauche → Droite (LTR)</option>
                                        <option value="rtl">Droite → Gauche (RTL)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="px-6 py-8 max-w-7xl mx-auto">
                <LocalesList
                    locales={filteredLocales}
                    onAction={(locale) => setSelectedLocale(locale)}
                    onSelectLocales={(selected) => setItemsToDelete(selected.map((l) => l.id))}
                    selectable={true}
                    loading={loading}
                    selectActions={[
                        {
                            label: "Supprimer",
                            onClick: (selected) => setItemsToDelete(selected.map((l) => l.id)),
                            className: "bg-red-600 text-white",
                        },
                    ]}
                />
            </div>

            {/* Menu modal */}
            {selectedLocale && (
                <MenuModal
                    title={selectedLocale.name}
                    isOpen={!!selectedLocale}
                    onClose={() => setSelectedLocale(null)}
                    icon={null}
                    menu={getActionsMenu(selectedLocale)}
                />
            )}

            {/* Modals suppression */}
            <DeleteConfirmationModal
                isOpen={!!localeToDelete}
                onClose={() => setLocaleToDelete(null)}
                onConfirm={handleDelete}
                title="Supprimer la langue"
                message={`Supprimer la langue "${localeToDelete?.name}" ?`}
                confirmText={isDeleting ? "Suppression..." : "Supprimer"}
            />

            <DeleteConfirmationModal
                isOpen={!!itemsToDelete && itemsToDelete.length > 0}
                onClose={() => setItemsToDelete(null)}
                onConfirm={handleDeleteMany}
                title="Suppression multiple"
                message={`Supprimer ${itemsToDelete?.length} langue(s) ?`}
            />
        </PageListLayout>
    );
}